import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axiosInstance from "../api/axios";
import logo from '../wysupp-icon-green-transparent.svg';
import AccountInfo from '../AccountInfo';
import { FaArrowLeft, FaUser, FaLock, FaEnvelope, FaUserTag, FaEye, FaEyeSlash, FaCopy, FaCheck, FaSyncAlt } from 'react-icons/fa';
import { useAuth } from '../AuthContext';

const UPPER   = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER   = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS  = '0123456789';
const SPECIAL = '!@#$%^&*()_+-=[]{}|;:,.<>?';
const ALL     = UPPER + LOWER + DIGITS + SPECIAL;

function generateSecurePassword() {
    const rand = (charset) => {
        const arr = new Uint32Array(1);
        crypto.getRandomValues(arr);
        return charset[arr[0] % charset.length];
    };

    // Guarantee at least one character from every required group
    const required = [rand(UPPER), rand(LOWER), rand(DIGITS), rand(SPECIAL)];

    const rest = Array.from({ length: 12 }, () => rand(ALL));
    const combined = [...required, ...rest];

    // Fisher-Yates shuffle using crypto randomness
    for (let i = combined.length - 1; i > 0; i--) {
        const arr = new Uint32Array(1);
        crypto.getRandomValues(arr);
        const j = arr[0] % (i + 1);
        [combined[i], combined[j]] = [combined[j], combined[i]];
    }

    return combined.join('');
}

export default function CreateUser() {
    const { user } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const handleGenerate = () => {
        setPassword(generateSecurePassword());
        setCopied(false);
    };

    const handleCopy = () => {
        if (!password) return;
        navigator.clipboard.writeText(password);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        const form = e.target;
        const formData = new FormData(form);
        const role = formData.get('role');

        const newUserDetails = { username, password, role, email };

        try {
            await axiosInstance.post('/api/admin/users', newUserDetails);
            setSuccess('User created successfully!');
            setUsername('');
            setPassword('');
            setEmail('');
            setCopied(false);
            form.reset();
        } catch (err) {
            console.error('Error creating user:', err.response || err);

            let errorMessage = 'Failed to create user.';
            const errorDetail = err.response?.data?.detail;

            if (typeof errorDetail === 'string') {
                errorMessage = errorDetail;
            } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
                const firstError = errorDetail[0];
                const failedField = firstError.loc[firstError.loc.length - 1];

                if (failedField === 'email') {
                    errorMessage = "Please enter a valid email address (e.g., user@company.com).";
                } else if (failedField === 'password') {
                    errorMessage = "Please ensure your password meets the required security criteria.";
                } else if (failedField === 'username') {
                    errorMessage = "Please enter a valid username.";
                } else {
                    const formattedField = failedField.charAt(0).toUpperCase() + failedField.slice(1);
                    errorMessage = `Please check your input for the ${formattedField} field.`;
                }
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="admin-page-container">
            <header className="dashboard-header">
                <Link to="/home" style={{ textDecoration: 'none' }}>
                    <div className="logo-section" style={{ cursor: 'pointer' }}>
                        <img src={logo} className="logo-icon" alt="Wysupp Logo" />
                        <span className="logo-text">Wysupp Validate</span>
                        <span className="logo-subtext">Data Quality Management</span>
                    </div>
                </Link>
                <AccountInfo />
            </header>

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/admin/manage-users')} className="back-button">
                        <FaArrowLeft /> Back to User Management
                    </button>
                </div>

                <div className="admin-form-wrapper">
                    <h1 className="form-title">Create New User</h1>
                    <p className="form-subtitle">Fill out the details below to create a new user account.</p>

                    <form className="admin-form" autoComplete="off" onSubmit={handleSubmit}>
                        {success && <div className="form-feedback success">{success}</div>}
                        {error && <div className="form-feedback error">{error}</div>}

                        <div className="input-group">
                            <FaUser className="input-icon" />
                            <input
                                type="text"
                                id="username"
                                placeholder="Username"
                                onChange={(e) => setUsername(e.target.value)}
                                value={username}
                                required
                            />
                        </div>

                        {/* Password — generated, never manually typed */}
                        <div className="password-section">
                            <div className="input-group" style={{ flex: 1 }}>
                                <FaLock className="input-icon" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    placeholder="Click 'Generate' to create a password"
                                    value={password}
                                    readOnly
                                    required
                                    style={{ paddingRight: '3rem', cursor: 'default' }}
                                />
                                <span
                                    className="password-toggle-icon"
                                    onClick={() => setShowPassword(v => !v)}
                                    title={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>

                            <div className="password-actions">
                                <button
                                    type="button"
                                    className="action-button generate-button"
                                    onClick={handleGenerate}
                                    title="Generate a secure password"
                                >
                                    <FaSyncAlt /> Generate
                                </button>
                                <button
                                    type="button"
                                    className="action-button copy-button"
                                    onClick={handleCopy}
                                    disabled={!password}
                                    title="Copy password to clipboard"
                                >
                                    {copied ? <><FaCheck /> Copied!</> : <><FaCopy /> Copy</>}
                                </button>
                            </div>
                        </div>

                        <div className="input-group">
                            <FaEnvelope className="input-icon" />
                            <input
                                type="email"
                                id="email"
                                placeholder="Email Address"
                                onChange={(e) => setEmail(e.target.value)}
                                value={email}
                                required
                            />
                        </div>
                        <div className="input-group">
                            <FaUserTag className="input-icon" />
                            <select id="role" name="role" required defaultValue="viewer">
                                <option value="viewer">Viewer</option>
                                <option value="editor">Editor</option>
                                <option value="manager">Manager</option>
                                <option value="admin" disabled={user?.role !== 'admin'}>
                                    Admin {user?.role !== 'admin' && '(Creation restricted)'}
                                </option>
                            </select>
                        </div>
                        <button
                            type="submit"
                            className="form-button"
                            disabled={loading || !password}
                            title={!password ? 'Generate a password first' : ''}
                        >
                            {loading ? 'Creating...' : 'Create User'}
                        </button>
                    </form>
                </div>
            </main>
            <style>{`
                :root {
                    --dark-bg: #1a202c;
                    --card-bg: #2d3748;
                    --text-color: #e2e8f0;
                    --light-text: #a0aec0;
                    --green-accent: #48bb78;
                    --input-bg: #1a202c;
                    --border-color: #4a5568;
                    --error-color: #fca5a5;
                    --error-bg: rgba(239, 68, 68, 0.1);
                    --success-color: #a7f3d0;
                    --success-bg: rgba(52, 211, 153, 0.1);
                    overflow-y: hidden;
                }

                .admin-page-container, .admin-page-container * {
                    box-sizing: border-box;
                }

                .admin-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                    display: flex;
                    flex-direction: column;
                }

                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 3rem;
                    background-color: var(--card-bg);
                    border-bottom: 1px solid var(--border-color);
                }
                .logo-section { display: flex; align-items: center; gap: 0.5rem; }
                .logo-icon { width: 30px; height: 30px; }
                .logo-text { font-size: 1.25rem; font-weight: 700; color: var(--green-accent); }

                .admin-main-content {
                    max-width: 700px;
                    margin: 2rem auto;
                    padding: 2rem;
                    width: 100%;
                    flex-grow: 1;
                }

                .password-toggle-icon {
                    position: absolute;
                    top: 50%;
                    right: 1rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                    cursor: pointer;
                }

                .page-header {
                    margin-bottom: 1.5rem;
                }
                .back-button {
                    background: transparent; color: var(--light-text); border: none;
                    padding: 0; font-weight: 600; cursor: pointer;
                    transition: color 0.2s; display: flex; align-items: center; gap: 0.5rem;
                }
                .back-button:hover { color: var(--text-color); }

                .admin-form-wrapper {
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    padding: 2.5rem;
                }

                .form-title {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: var(--text-color);
                    margin-bottom: 0.5rem;
                }
                .form-subtitle {
                    font-size: 1rem;
                    color: var(--light-text);
                    margin-bottom: 2rem;
                }

                .admin-form {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .form-feedback {
                    width: 100%; padding: 0.75rem; border-radius: 6px;
                    font-size: 0.9rem; font-weight: 500;
                }
                .form-feedback.success { background-color: var(--success-bg); color: var(--success-color); }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); }

                .input-group { position: relative; }
                .input-group input {
                    width: 100%;
                    height: 1.5rem;
                    padding: 1rem 1rem 1rem 3rem;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    color: var(--text-color);
                    font-size: 1rem;
                    transition: border-color 0.2s;
                }

                .input-group select {
                    width: 100%;
                    padding: 1rem 1rem 1rem 3rem;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    color: var(--text-color);
                    font-size: 1rem;
                    transition: border-color 0.2s;
                    appearance: none;
                    background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23a0aec0%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E');
                    background-repeat: no-repeat;
                    background-position: right 1rem center;
                    background-size: 0.65em auto;
                }
                .input-group input:focus, .input-group select:focus {
                    outline: none;
                    border-color: var(--green-accent);
                }

                .input-icon {
                    position: absolute;
                    top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                }

                /* Password row */
                .password-section {
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                }

                .password-actions {
                    display: flex;
                    gap: 0.75rem;
                }

                .action-button {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                    padding: 0.55rem 1rem;
                    border-radius: 6px;
                    font-size: 0.875rem;
                    font-weight: 600;
                    cursor: pointer;
                    border: none;
                    transition: background-color 0.2s, opacity 0.2s;
                    flex: 1;
                    justify-content: center;
                }

                .generate-button {
                    background-color: #4a5568;
                    color: var(--text-color);
                }
                .generate-button:hover { background-color: #606878; }

                .copy-button {
                    background-color: var(--green-accent);
                    color: var(--dark-bg);
                }
                .copy-button:hover:not(:disabled) { background-color: #38a169; }
                .copy-button:disabled { opacity: 0.4; cursor: not-allowed; }

                .form-button {
                    background-color: var(--green-accent); color: var(--dark-bg);
                    border: none; border-radius: 6px; padding: 1rem;
                    font-size: 1rem; font-weight: 600; cursor: pointer;
                    transition: background-color 0.2s; margin-top: 1rem;
                }
                .form-button:hover:not(:disabled) { background-color: #38a169; }
                .form-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }
                .logo-subtext {
                    font-size: 0.75rem;
                    color: var(--light-text);
                    margin-left: 0.5rem;
                    padding-left: 0.5rem;
                    border-left: 1px solid var(--light-text);
                }
            `}</style>
        </div>
    );
}
